/**
 * Batch timing formatter
 *
 * Converts a batch `schedule` array like:
 *   [{ day: 'Monday', startTime: '9:00', endTime: '12:00' },
 *    { day: 'Tuesday', startTime: '9:00', endTime: '12:00' }, ...]
 *
 * Into a human-readable string like:
 *   "Monday - Friday (9:00 AM - 12:00 PM)"
 *
 * Rules:
 *  - Days that share the same start/end time are grouped together.
 *  - If those days are consecutive in the week, they are shown as a range
 *    e.g. "Monday - Friday".
 *  - Otherwise they are listed individually e.g. "Monday, Wednesday, Friday".
 *  - Multiple distinct time groups are separated by " | ".
 *  - Times are shown in 12-hour AM/PM format.
 *
 * `reformatLegacyTimings` parses strings already stored in the old format
 * ("Monday 09:00–16:00, Tuesday 09:00–16:00, ...") and converts them to the
 * new format without needing access to batch schedule data.
 */

const DAY_ORDER = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** Convert "9:00" or "13:30" → "9:00 AM" / "1:30 PM" */
export function formatTime12(time24) {
  if (!time24) return '';
  const [hStr, mStr] = time24.split(':');
  const h = parseInt(hStr, 10);
  const m = parseInt(mStr, 10);
  if (isNaN(h) || isNaN(m)) return time24;
  const period = h >= 12 ? 'PM' : 'AM';
  const hour   = h % 12 || 12;
  return `${hour}:${String(m).padStart(2, '0')} ${period}`;
}

/**
 * Build a readable timing string from a batch schedule array.
 * Returns '' when schedule is empty or falsy.
 */
export function formatBatchTimings(schedule) {
  if (!Array.isArray(schedule) || schedule.length === 0) return '';

  // Group entries by "startTime|endTime"
  const groups = {};
  schedule.forEach((s) => {
    const key = `${s.startTime}|${s.endTime}`;
    if (!groups[key]) {
      groups[key] = { startTime: s.startTime, endTime: s.endTime, days: [] };
    }
    groups[key].days.push(s.day);
  });

  const parts = Object.values(groups).map(({ startTime, endTime, days }) => {
    // Sort by canonical week order
    const sorted = [...days].sort(
      (a, b) => DAY_ORDER.indexOf(a) - DAY_ORDER.indexOf(b)
    );

    // Check if every day in the sorted list is consecutive
    const indices = sorted.map((d) => DAY_ORDER.indexOf(d));
    const isConsecutive =
      sorted.length > 1 &&
      indices.every((idx, i) => i === 0 || idx === indices[i - 1] + 1);

    const dayStr = isConsecutive
      ? `${sorted[0]} - ${sorted[sorted.length - 1]}`
      : sorted.join(', ');

    return `${dayStr} (${formatTime12(startTime)} - ${formatTime12(endTime)})`;
  });

  return parts.join(' | ');
}

/**
 * Reformat a timing string that may be stored in the old DB format:
 *   "Monday 09:00–16:00, Tuesday 09:00–16:00, ..."
 *   "Monday 9:00-12:00, Wednesday 9:00-12:00"
 *
 * If the string already contains AM/PM (new format) it is returned unchanged.
 * If parsing fails the original string is returned as a safe fallback.
 */
export function reformatLegacyTimings(timingStr) {
  if (!timingStr) return '';

  // Already in new format — nothing to do
  if (/\b(AM|PM)\b/i.test(timingStr)) return timingStr;

  // Split on ", " boundaries (each token is "DayName HH:MM–HH:MM")
  const tokens = timingStr.split(/,\s*/);
  const schedule = [];

  for (const token of tokens) {
    // Match "DayName HH:MM[–\-—]HH:MM"
    const match = token.trim().match(
      /^([A-Za-z]+)\s+(\d{1,2}:\d{2})\s*[–\-—]\s*(\d{1,2}:\d{2})$/
    );
    if (!match) return timingStr; // unrecognised format — return original
    schedule.push({ day: match[1], startTime: match[2], endTime: match[3] });
  }

  if (schedule.length === 0) return timingStr;
  return formatBatchTimings(schedule) || timingStr;
}
